import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, momentLocalizer, ToolbarProps, View } from 'react-big-calendar';
import { Box, Divider, Drawer, Group, LoadingOverlay, Select, Stack, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { DatePicker } from '@mantine/dates';
import { IconFilterOff } from '@tabler/icons-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './reactBigCalendarOverrides.css';
import moment from 'moment-timezone';
import { useSearchParams } from 'react-router';
import SchedulingCalendarToolbar from './components/SchedulingCalendarToolbar';
import { AppointmentDrawer } from './Appointment/AppointmentDrawer';
import { BlockDrawer } from './Block/BlockDrawer';
import { AvailabilityDrawer } from './Availability/AvailabilityDrawer';
import { AvailabilityList } from './components/AvailabilityList';
import { AvailabilityListFilters } from './components/AvailabilityListFilters';
import { AppointmentDetailPopover } from './components/AppointmentDetailPopover';
import { BlockDetailPopover } from './components/BlockDetailPopover';
import { useAppointments } from './hooks/useAppointments';
import { useBlockSlots } from './hooks/useBlockSlots';
import {
  AppointmentEvent,
  AppointmentDetailData,
  AppointmentWithEncounter,
  BlockSlotDetailData,
  BlockSlotEvent,
  PopoverPosition,
  AvailabilityBackgroundEvent,
  UseFindAvailabilityFilters,
} from './types';
import { getActivityDefinitionColor, getAppointmentColor } from './utils/appointmentColors';
import classes from './SchedulingPage.module.css';
import { createReference, formatCodeableConcept, formatHumanName, getReferenceString } from '@medplum/core';
import { Appointment, Practitioner, Location, PractitionerRole, Reference } from '@medplum/fhirtypes';
import {
  getWeeklyAvailabilityEntry,
  loadAvailabilityResources,
  loadFilterResources,
  loadOneTimeAvailabilityResources,
  mapSlotToAvailabilityEntry,
} from './utils/availability';
import { alert } from '@/services/alert';
import { BLOCK_APPOINTMENT_TYPE_CODE, ProviderRole, PROVIDER_ROLE_OPTIONS } from '@bariendo/lib/constants';
import { transformAppointmentToEvent } from './utils/appointments';
import SchedulingFilter from './components/SchedulingFilter';
import { useFindAvailability } from './hooks/useFindAvailability';
import { ProviderAvailability } from './Availability/types';
import { useMedplum } from '@medplum/react';
import useDefaultPractitioner from './hooks/useDefaultPractitioner';
import { AvailabilityType, AvailabilityTypeEnum } from '@bariendo/lib/scheduling';
import { SchedulingPageProvider } from './SchedulingPageContext';
import { generateTimezoneOptions, getBrowserTimezone } from './utils/common';
import { buildAppointmentTypeCodings, mapPractitionersToWithRole } from './utils/schedulingShared';
import { useAlwaysLatestSelectedAppointment } from './hooks/useAlwaysLatestSelectedAppointment';

// If the component was already loaded we don't need to load the one-time availabilities again.
// This does not keep track of items that have been removed by the filter
const reuseOneTimeAlreadyLoaded = (prev: ProviderAvailability[], newEntries: ProviderAvailability[]) => {
  return newEntries.map((e) => {
    const prevEntry = prev.find((p) => p.providerId === e.providerId);
    if (!prevEntry) return e;
    return {
      ...e,
      availabilities: [
        // The new entries should only have weekly availabilities anyway
        ...e.availabilities.filter((a) => a.type === AvailabilityTypeEnum.WEEKLY),
        ...prevEntry.availabilities.filter((a) => a.type === AvailabilityTypeEnum.ONETIME),
      ],
    };
  });
};

export function SchedulingPage(): ReactElement {
  const medplum = useMedplum();
  const [searchParams, setSearchParams] = useSearchParams();
  const showingAvailabilityList = searchParams.get('view') === 'availability';
  const setShowingAvailabilityList = useCallback(
    (value: boolean) => {
      setSearchParams((prev) => {
        if (value) {
          prev.set('view', 'availability');
        } else {
          prev.delete('view');
        }
        return prev;
      });
    },
    [setSearchParams]
  );
  type CalendarEvent = AppointmentEvent | BlockSlotEvent | AvailabilityBackgroundEvent;
  const calendarRef = useRef<Calendar<CalendarEvent, object>>(null);
  const [view, setView] = useState<View>('week');
  const [date, setDate] = useState<Date>(new Date());
  const [showingFilterForm, setShowingFilterForm] = useState(false);
  const [appointmentDrawerOpened, setAppointmentDrawerOpened] = useState(false);
  const [availabilityDrawerOpened, setAvailabilityDrawerOpened] = useState(false);
  const [blockDrawerOpened, setBlockDrawerOpened] = useState(false);
  const [showingAvailabilityListFilters, setShowingAvailabilityListFilters] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentDetailData | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<BlockSlotDetailData | null>(null);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [blockPopoverOpened, setBlockPopoverOpened] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [availabilityEntries, setAvailabilityEntries] = useState<ProviderAvailability[]>([]);
  const [practitionerRoles, setPractitionerRoles] = useState<PractitionerRole[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [showAppointments, setShowAppointments] = useState(true);
  const [showAvailability, setShowAvailability] = useState(true);
  const [editAvailabilityContext, setEditAvailabilityContext] = useState<{
    providerId: string;
    availabilityId: string;
    type: AvailabilityType;
  } | null>(null);
  const [editAppointmentContext, setEditAppointmentContext] = useState<{
    appointmentRef: string;
  } | null>(null);
  const [editBlockContext, setEditBlockContext] = useState<{
    slotRef: string;
    detachFromSeries?: boolean;
  } | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState(getBrowserTimezone);

  const localizer = useMemo(() => {
    moment.tz.setDefault(selectedTimezone);
    return momentLocalizer(moment);
  }, [selectedTimezone]);
  const calendarAnchorDate = useMemo(
    () => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0),
    [date]
  );
  const dayViewBounds = useMemo(() => {
    const anchor = moment.tz(
      {
        year: calendarAnchorDate.getFullYear(),
        month: calendarAnchorDate.getMonth(),
        day: calendarAnchorDate.getDate(),
        hour: 12,
      },
      selectedTimezone
    );

    return {
      min: anchor.clone().startOf('day').toDate(),
      max: anchor.clone().endOf('day').toDate(),
    };
  }, [calendarAnchorDate, selectedTimezone]);

  const practitionersWithRoleLabels = useMemo(
    () => mapPractitionersToWithRole(practitioners, practitionerRoles),
    [practitioners, practitionerRoles]
  );

  const appointmentsFilters = useMemo(() => {
    const practitionersWithRoles = selectedProvider
      .map((p) => practitionerRoles.find((pr) => getReferenceString(pr) === p))
      .filter(
        (role): role is PractitionerRole & { practitioner: Reference<Practitioner> } => !!role && !!role.practitioner
      )
      .map((role) => role.practitioner);

    return {
      roles: selectedRole as ProviderRole[],
      practitioners: practitionersWithRoles,
      serviceTypes: selectedServiceType,
      statuses: selectedStatuses,
      locations: selectedLocation,
    };
  }, [selectedRole, selectedProvider, selectedServiceType, selectedStatuses, selectedLocation, practitionerRoles]);

  const { isDefaultPractitionerSelected } = useDefaultPractitioner(practitionerRoles, setSelectedProvider);

  // Fetch appointments for the current provider
  const {
    events: appointmentEvents,
    loading: appointmentsLoading,
    refresh: refreshAppointments,
    getActivityDefinitionForAppointmentType,
    activityCodeSystem,
  } = useAppointments({
    date,
    view,
    timezone: selectedTimezone,
    appointmentsFilters,
    enabled: isDefaultPractitionerSelected,
  });

  const {
    events: blockSlotEvents,
    loading: blockSlotsLoading,
    refresh: refreshBlockSlots,
  } = useBlockSlots({
    date,
    view,
    timezone: selectedTimezone,
    appointmentsFilters,
    practitionerRoles,
    practitioners,
    enabled: isDefaultPractitionerSelected,
  });

  const calendarEvents = useMemo(
    () => [...appointmentEvents, ...blockSlotEvents],
    [appointmentEvents, blockSlotEvents]
  );

  const dayViewResources = useMemo(() => {
    if (view !== 'day') return undefined;

    // 1) If a provider filter is active, show only those providers.
    if (appointmentsFilters.practitioners?.length) {
      return appointmentsFilters.practitioners.map((ref) => {
        const p = practitioners.find((pract) => getReferenceString(pract) === getReferenceString(ref));
        return {
          id: getReferenceString(ref),
          title: p ? formatHumanName(p.name?.[0]) : getReferenceString(ref),
        };
      });
    }

    // 2) If a role filter is active, show ALL providers with that role
    // even if they have no appointments/availability on the current day.
    if (appointmentsFilters.roles?.length) {
      const selectedRoleSet = new Set(appointmentsFilters.roles);

      const roleMatched = practitionersWithRoleLabels.filter((p) => {
        return p.roleCode !== undefined && selectedRoleSet.has(p.roleCode);
      });

      return roleMatched
        .map((p) => {
          const id = getReferenceString(p as Practitioner);
          return {
            id,
            title: (p.name?.[0] ? formatHumanName(p.name[0]) : '') ?? id,
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title));
    }

    const resourceToTitle = new Map<string, string>();
    for (const event of calendarEvents) {
      if ('appointmentResource' in event) {
        const providers = (event as AppointmentEvent).providers;
        if (!providers?.length) continue;
        for (const p of providers) {
          const resourceId = getReferenceString(p as Practitioner);
          if (!resourceId || resourceToTitle.has(resourceId)) continue;
          const title =
            'name' in p && p.name ? formatHumanName(p.name[0]) : 'display' in p && p.display ? p.display : resourceId;
          resourceToTitle.set(resourceId, title);
        }
      } else if ('slotResource' in event) {
        const providers = (event as BlockSlotEvent).providers;
        if (!providers?.length) continue;
        for (const p of providers) {
          const resourceId = getReferenceString(p as Practitioner);
          if (!resourceId || resourceToTitle.has(resourceId)) continue;
          const title =
            'name' in p && p.name ? formatHumanName(p.name[0]) : 'display' in p && p.display ? p.display : resourceId;
          resourceToTitle.set(resourceId, title);
        }
      }
    }
    if (!resourceToTitle.size) return undefined;

    return Array.from(resourceToTitle.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, title]) => ({ id, title }));
  }, [
    view,
    appointmentsFilters.practitioners,
    appointmentsFilters.roles,
    practitioners,
    practitionersWithRoleLabels,
    calendarEvents,
  ]);

  const getCalendarEventResourceId = useCallback((event: CalendarEvent): string | undefined => {
    // Availability background events may include `resource` when we request per-provider availability.
    const maybeResource = (event as { resource?: unknown }).resource;
    if (typeof maybeResource === 'string') return maybeResource;

    if ('slotResource' in event) {
      const block = event as BlockSlotEvent;
      return block.resource ?? (block.providers[0] ? (getReferenceString(block.providers[0]) ?? undefined) : undefined);
    }

    // Appointments: render under the first provider column.
    const providers = (event as AppointmentEvent).providers;
    const firstProvider = providers?.[0];
    return firstProvider ? (getReferenceString(firstProvider) ?? undefined) : undefined;
  }, []);

  const dayViewCalendarProps = useMemo(() => {
    if (!dayViewResources) return undefined;

    // Typing to react-big-calendar accepts object, so we need to cast it to the correct type
    return {
      resources: dayViewResources,
      resourceIdAccessor: (r: object) => (r as { id: string }).id,
      resourceTitleAccessor: (r: object) => (r as { title: string }).title,
      resourceAccessor: (event: CalendarEvent) => getCalendarEventResourceId(event),
    };
  }, [dayViewResources, getCalendarEventResourceId]);

  const findAvailabilityFilters: UseFindAvailabilityFilters = useMemo(() => {
    const practitionerRefs = appointmentsFilters.practitioners;
    const resourceIdsFromColumns = dayViewResources?.map((r) => r.id) ?? [];
    const practitionerRefsToUse =
      practitionerRefs?.length > 0
        ? practitionerRefs
        : resourceIdsFromColumns.length > 0
          ? resourceIdsFromColumns.map((id) => ({ reference: id }) as Reference<Practitioner>)
          : [];
    const practitionersRoles =
      practitionerRefsToUse.length > 0
        ? practitionerRoles
            .filter(
              (pr) =>
                pr.practitioner &&
                practitionerRefsToUse.some((p) => getReferenceString(p) === getReferenceString(pr.practitioner!))
            )
            .map((pr) => createReference(pr))
        : selectedProvider.map((p) => createReference(practitionerRoles.find((pr) => getReferenceString(pr) === p)!));
    return {
      practitionersRoles,
      roles: selectedRole as ProviderRole[],
      serviceTypes: selectedServiceType,
      locations: selectedLocation,
    };
  }, [
    practitionerRoles,
    selectedProvider,
    selectedRole,
    selectedServiceType,
    selectedLocation,
    appointmentsFilters.practitioners,
    dayViewResources,
  ]);

  const {
    backgroundEvents,
    isAvailabilityLoading,
    refresh: refreshAvailability,
  } = useFindAvailability(date, view, isDefaultPractitionerSelected, findAvailabilityFilters, selectedTimezone);

  // Background events come from API with resourceId per provider when using day view columns
  const backgroundEventsForCalendar = backgroundEvents;

  const handleSelectEvent = (event: CalendarEvent, e: React.SyntheticEvent) => {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const position: PopoverPosition = {
      x: rect.right + 12,
      y: rect.top + rect.height / 2,
      eventElement: target,
    };

    if ('slotResource' in event) {
      const blockEvent = event as BlockSlotEvent;
      setSelectedBlock({
        slotResource: blockEvent.slotResource,
        providers: blockEvent.providers,
        title: typeof blockEvent.title === 'string' ? String(blockEvent.title ?? '') : 'Block',
      });
      setSelectedAppointment(null);
      setPopoverPosition(position);
      setBlockPopoverOpened(true);
      setPopoverOpened(false);
      return;
    }

    // Availability background event (no appointment or slot)
    if (!('appointmentResource' in event)) {
      return;
    }

    const apptEvent = event as AppointmentEvent;

    const title = typeof apptEvent.title === 'string' ? String(apptEvent.title ?? '') : undefined;
    const transformedEvent = transformAppointmentToEvent(
      apptEvent.appointmentResource,
      getActivityDefinitionForAppointmentType
    );

    setSelectedAppointment({ ...transformedEvent, title, appointmentResource: apptEvent.appointmentResource });
    setSelectedBlock(null);
    setPopoverPosition(position);
    setPopoverOpened(true);
    setBlockPopoverOpened(false);
  };

  const handleClosePopover = () => {
    setPopoverOpened(false);
    setSelectedAppointment(null);
    setPopoverPosition(null);
  };

  const handleCloseBlockPopover = () => {
    setBlockPopoverOpened(false);
    setSelectedBlock(null);
    setPopoverPosition(null);
  };

  const handleDeleteAppointment = () => {
    refreshAppointments();
    refreshAvailability();
    handleClosePopover();
  };

  const handleBlockSaveSuccess = useCallback(() => {
    refreshAvailability();
    refreshAppointments();
    refreshBlockSlots();
  }, [refreshAvailability, refreshAppointments, refreshBlockSlots]);

  const handleStatusChange = (updatedAppointment: Appointment) => {
    setSelectedAppointment((prev) => {
      if (!prev || prev.appointmentResource.id !== updatedAppointment.id) return prev;
      const transformed = transformAppointmentToEvent(updatedAppointment, getActivityDefinitionForAppointmentType);
      // Preserve encounter from prev - patchResource returns raw Appointment without _revinclude data
      const appointmentResource: AppointmentWithEncounter = {
        ...updatedAppointment,
        encounter: prev.appointmentResource.encounter,
      };
      return {
        ...prev,
        status: transformed.status,
        appointmentResource,
      };
    });
    refreshAppointments();
    refreshAvailability();
  };

  useAlwaysLatestSelectedAppointment({
    appointmentEvents,
    selectedAppointment,
    setSelectedAppointment,
    getActivityDefinitionForAppointmentType,
    transformAppointmentToEvent,
  });

  useEffect(() => {
    if (!selectedBlock?.slotResource.id) return;
    const latest = blockSlotEvents.find((ev) => ev.slotResource.id === selectedBlock.slotResource.id);
    if (!latest) return;
    setSelectedBlock((prev) =>
      prev && prev.slotResource.id === latest.slotResource.id
        ? { ...prev, slotResource: latest.slotResource, providers: latest.providers }
        : prev
    );
  }, [blockSlotEvents, selectedBlock?.slotResource.id]);

  const eventStyleGetter = (event: CalendarEvent) => {
    if ('appointmentType' in event && event.appointmentType === 'availability') {
      return {
        style: {
          border: 'none',
          borderRadius: '5px',
          boxShadow: 'none',
          backgroundColor: 'rgba(37, 129, 149, 0.3)',
          opacity: 1,
        },
      };
    }
    if ('slotResource' in event) {
      const blockColors = getActivityDefinitionColor(
        getActivityDefinitionForAppointmentType(BLOCK_APPOINTMENT_TYPE_CODE)
      );
      return {
        style: {
          backgroundColor: blockColors.backgroundColor,
          borderColor: blockColors.borderColor,
          color: blockColors.textColor,
          borderRadius: '4px',
          border: `1px solid ${blockColors.borderColor}`,
          padding: '2px 5px',
        },
      };
    }
    const colors = getAppointmentColor(
      (event as AppointmentEvent).appointmentResource,
      getActivityDefinitionForAppointmentType
    );
    return {
      style: {
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        color: colors.textColor,
        borderRadius: '4px',
        border: `1px solid ${colors.borderColor}`,
        padding: '2px 5px',
      },
    };
  };

  const handleAvailabilityListOpenChange = (open: boolean) => {
    setShowingAvailabilityList(open);
    const showFilters = selectedLocation.length > 0 || selectedRole.length > 0 || selectedProvider.length > 0;
    if (showFilters) {
      setShowingAvailabilityListFilters(true);
      setShowingFilterForm(true);
    } else {
      setShowingAvailabilityListFilters(false);
      setShowingFilterForm(false);
    }
  };

  const ToolbarWrapper = (props: ToolbarProps<CalendarEvent, object>) => (
    <SchedulingCalendarToolbar
      {...props}
      isShowingFilterForm={showingFilterForm}
      onSetShowingFilterForm={setShowingFilterForm}
      onSetView={setView}
      isShowingAvailabilityList={showingAvailabilityList}
      onSetShowingAvailabilityList={(value: boolean) => handleAvailabilityListOpenChange(value)}
      onOpenAppointment={() => {
        setEditBlockContext(null);
        setBlockDrawerOpened(false);
        setAppointmentDrawerOpened(true);
      }}
      onOpenAvailability={handleOpenAvailability}
      onOpenBlock={handleOpenBlock}
    />
  );

  const handleEditAvailability = useCallback((providerId: string, availabilityId: string, type: AvailabilityType) => {
    setEditAvailabilityContext({ providerId, availabilityId, type });
    setAvailabilityDrawerOpened(true);
  }, []);

  const handleOpenAvailability = useCallback(() => {
    setEditAvailabilityContext(null);
    setAvailabilityDrawerOpened(true);
  }, []);

  const handleOpenBlock = useCallback(() => {
    setEditAppointmentContext(null);
    setEditBlockContext(null);
    setBlockDrawerOpened(true);
  }, []);

  const handleCloseAvailabilityDrawer = useCallback(() => {
    setEditAvailabilityContext(null);
    setAvailabilityDrawerOpened(false);
  }, []);

  const handleClearFilters = () => {
    setSelectedRole([]);
    setSelectedProvider([]);
    setSelectedLocation([]);
  };

  const handleEditAppointment = useCallback(() => {
    if (selectedAppointment?.appointmentResource) {
      const appointmentRef = getReferenceString(selectedAppointment.appointmentResource) ?? '';
      setEditBlockContext(null);
      setEditAppointmentContext({ appointmentRef });
      setAppointmentDrawerOpened(true);
      setBlockDrawerOpened(false);
      handleClosePopover();
    }
  }, [selectedAppointment]);

  const handleEditBlock = useCallback(() => {
    if (!selectedBlock?.slotResource) return;
    const slotRef = getReferenceString(selectedBlock.slotResource) ?? `Slot/${selectedBlock.slotResource.id}`;
    setEditAppointmentContext(null);
    setEditBlockContext({ slotRef });
    setBlockDrawerOpened(true);
    setAppointmentDrawerOpened(false);
    handleCloseBlockPopover();
  }, [selectedBlock]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const resources = await loadFilterResources(medplum);
        setPractitioners(resources.practitioners);
        setLocations(resources.locations);
        setPractitionerRoles(resources.practitionerRoles);
      } catch (error) {
        alert.error("Couldn't load filter resources: " + (error instanceof Error ? error.message : 'Unknown error'));
      }
    };

    loadFilters();
  }, [medplum]);

  useEffect(() => {
    const loadAvailabilities = async () => {
      if (!practitioners.length || !practitionerRoles.length || !locations.length) {
        return;
      }

      try {
        const resources = await loadAvailabilityResources(medplum, {
          roles: selectedRole,
          providers: selectedProvider as Reference<PractitionerRole>[],
        });
        const entries = getWeeklyAvailabilityEntry(resources.schedules, practitionerRoles, practitioners, locations);
        setAvailabilityEntries((prev) => reuseOneTimeAlreadyLoaded(prev, entries));
      } catch (error) {
        alert.error(
          "Couldn't load availability entries: " + (error instanceof Error ? error.message : 'Unknown error')
        );
      }
    };

    loadAvailabilities();
  }, [medplum, selectedRole, selectedProvider, practitioners, practitionerRoles, locations]);

  const providers = useMemo(() => {
    if (selectedLocation.length === 0) return availabilityEntries;
    return availabilityEntries
      .map((entry) => {
        const filteredAvailabilities = entry.availabilities.filter((availability) =>
          selectedLocation.includes(availability.locationReference ?? '')
        );
        return { ...entry, availabilities: filteredAvailabilities };
      })
      .filter((entry) => entry.availabilities.length > 0);
  }, [availabilityEntries, selectedLocation]);

  const locationNames = useMemo(
    () =>
      locations.map((l) => ({ value: getReferenceString(l), label: l.name })).filter((l) => l.value && l.label) as {
        value: string;
        label: string;
      }[],
    [locations]
  );

  const availabilityDrawerEditProps = useMemo(() => {
    if (!editAvailabilityContext) {
      return {
        initialPractitionerOverride: null as Practitioner | null,
        initialAvailabilityType: AvailabilityTypeEnum.WEEKLY,
        slotIdToEdit: undefined as string | undefined,
      };
    }
    const practitioner =
      practitioners.find((p) => getReferenceString(p) === editAvailabilityContext.providerId) ?? null;
    const isWeekly = editAvailabilityContext.type === AvailabilityTypeEnum.WEEKLY;
    const slotIdToEdit = isWeekly ? undefined : editAvailabilityContext.availabilityId;
    return {
      initialPractitionerOverride: practitioner,
      initialAvailabilityType: isWeekly ? AvailabilityTypeEnum.WEEKLY : AvailabilityTypeEnum.ONETIME,
      slotIdToEdit: slotIdToEdit,
    };
  }, [editAvailabilityContext, practitioners]);

  const filterProps = {
    roles: PROVIDER_ROLE_OPTIONS,
    locations: locationNames,
    selectedRole: selectedRole,
    selectedProvider: selectedProvider,
    selectedLocation: selectedLocation,
    onRoleChange: setSelectedRole,
    onProviderChange: setSelectedProvider,
    onLocationChange: setSelectedLocation,
    onClearFilters: handleClearFilters,
    practitioners: practitionersWithRoleLabels,
    practitionerRoles,
  };

  const activityDefinitionsList = useMemo(() => {
    if (!activityCodeSystem) return [];
    return activityCodeSystem
      .map((coding) => {
        const activityDefinition = getActivityDefinitionForAppointmentType(coding.code);
        if (!activityDefinition || !coding.code) return null;
        const colors = getActivityDefinitionColor(activityDefinition);
        return {
          code: coding.code,
          value: coding.code,
          label: formatCodeableConcept(activityDefinition.code),
          ...colors,
        };
      })
      .filter((item) => item !== null);
  }, [activityCodeSystem, getActivityDefinitionForAppointmentType]);

  const appointmentTypeCodings = useMemo(
    () => buildAppointmentTypeCodings(activityCodeSystem, getActivityDefinitionForAppointmentType),
    [activityCodeSystem, getActivityDefinitionForAppointmentType]
  );

  const onNavigate = useCallback((newDate: Date, newView: View) => {
    setDate(new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate()));
    setView(newView);
  }, []);

  const onSelectSlot = useCallback(() => {
    handleClosePopover();
    handleCloseBlockPopover();
  }, [handleClosePopover, handleCloseBlockPopover]);

  const getOneTimeAvailability = useCallback(
    async (provider: ProviderAvailability, providerScheduleReference: string) => {
      const slots = await loadOneTimeAvailabilityResources(medplum, providerScheduleReference);
      const oneTimeEntries = slots.map((slot) => mapSlotToAvailabilityEntry(slot, locations));

      const weeklyAvailabilities = provider.availabilities.filter((a) => a.type === AvailabilityTypeEnum.WEEKLY);

      const updatedAvailabilities = [...weeklyAvailabilities, ...oneTimeEntries];
      setAvailabilityEntries((prev: ProviderAvailability[]) =>
        prev.map((p) =>
          p.providerId === provider.providerId
            ? {
                ...p,
                availabilities: updatedAvailabilities,
              }
            : p
        )
      );
    },
    [medplum, locations]
  );

  const handleAvailabilitySaveSuccess = useCallback(
    (providerId: string | undefined, type: AvailabilityType) => {
      if (!providerId) {
        return;
      }
      refreshAvailability();
      if (type === 'onetime') {
        const provider = availabilityEntries.find((p) => p.providerId === providerId);
        if (provider?.providerScheduleReference) {
          getOneTimeAvailability(provider, provider.providerScheduleReference).catch((error) => {
            alert.error("Couldn't refresh availability: " + (error instanceof Error ? error.message : 'Unknown error'));
          });
        }
      } else {
        loadAvailabilityResources(medplum, {
          roles: selectedRole,
          providers: selectedProvider as Reference<PractitionerRole>[],
        })
          .then((resources) => {
            const newEntries = getWeeklyAvailabilityEntry(
              resources.schedules,
              practitionerRoles,
              practitioners,
              locations
            );
            setAvailabilityEntries((prev) => reuseOneTimeAlreadyLoaded(prev, newEntries));
          })
          .catch((error) => {
            alert.error("Couldn't refresh availability: " + (error instanceof Error ? error.message : 'Unknown error'));
          });
      }
    },
    [
      medplum,
      selectedRole,
      selectedProvider,
      practitionerRoles,
      practitioners,
      locations,
      availabilityEntries,
      getOneTimeAvailability,
      refreshAvailability,
    ]
  );

  const contextValue = useMemo(
    () => ({
      appointmentTypeCodings,
      getActivityDefinitionForAppointmentType,
    }),
    [appointmentTypeCodings, getActivityDefinitionForAppointmentType]
  );

  if (showingAvailabilityList) {
    return (
      <SchedulingPageProvider value={contextValue}>
        <Box display="flex" h="100%" style={{ flexDirection: 'column' }}>
          <SchedulingCalendarToolbar
            view={view}
            date={date}
            onNavigate={(_action, _date) => {}}
            views={{}}
            label=""
            localizer={localizer}
            onView={() => {}}
            isShowingFilterForm={showingAvailabilityListFilters}
            onSetShowingFilterForm={setShowingAvailabilityListFilters}
            onSetView={setView}
            isShowingAvailabilityList={showingAvailabilityList}
            onSetShowingAvailabilityList={(value: boolean) => handleAvailabilityListOpenChange(value)}
            onOpenAppointment={() => {
              setEditBlockContext(null);
              setBlockDrawerOpened(false);
              setAppointmentDrawerOpened(true);
            }}
            onOpenAvailability={handleOpenAvailability}
            onOpenBlock={() => {
              setEditAppointmentContext(null);
              setEditBlockContext(null);
              setBlockDrawerOpened(true);
            }}
          />
          <Divider />
          <Box display="flex" style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            {!isMobile && showingAvailabilityListFilters && (
              <Box
                w={300}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: '1px solid #e0e0e0',
                  backgroundColor: '#ffffff',
                  alignSelf: 'stretch',
                }}
              >
                <AvailabilityListFilters {...filterProps} />
              </Box>
            )}
            <Box style={{ flex: 1, overflow: 'hidden' }}>
              <AvailabilityList
                providers={providers}
                onEditAvailability={handleEditAvailability}
                getOneTimeAvailability={getOneTimeAvailability}
              />
            </Box>
          </Box>
          {isMobile && (
            <Drawer
              opened={showingAvailabilityListFilters}
              onClose={() => setShowingAvailabilityListFilters(false)}
              position="left"
              size="xs"
              padding="md"
              title="Filters"
            >
              <AvailabilityListFilters {...filterProps} />
            </Drawer>
          )}

          <AppointmentDrawer
            opened={appointmentDrawerOpened}
            onClose={() => {
              setAppointmentDrawerOpened(false);
            }}
            practitioners={practitionersWithRoleLabels}
            appointmentReference={editAppointmentContext?.appointmentRef ?? null}
          />
          <BlockDrawer
            opened={blockDrawerOpened}
            onClose={() => {
              setBlockDrawerOpened(false);
              setEditBlockContext(null);
            }}
            onSaveSuccess={handleBlockSaveSuccess}
            practitioners={practitionersWithRoleLabels}
            practitionerRoles={practitionerRoles}
            slotReference={editBlockContext?.slotRef ?? null}
            detachFromSeries={editBlockContext?.detachFromSeries ?? false}
          />
          <AvailabilityDrawer
            opened={availabilityDrawerOpened}
            onClose={handleCloseAvailabilityDrawer}
            onSaveSuccess={handleAvailabilitySaveSuccess}
            practitioners={practitionersWithRoleLabels}
            {...availabilityDrawerEditProps}
          />
        </Box>
      </SchedulingPageProvider>
    );
  }

  return (
    <SchedulingPageProvider value={contextValue}>
      <Box className={classes.schedulingPageRoot} style={{ position: 'relative' }}>
        {showingFilterForm && (
          <Stack w={300} h="100%">
            <Group p={16} w="100%" style={{ borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
              <UnstyledButton
                className={`${classes.actionButton} ${classes.actionGroup}`}
                c="#246A7A"
                onClick={() => setShowingFilterForm(false)}
              >
                <IconFilterOff size={16} />
              </UnstyledButton>
            </Group>
            <Stack w="100%" p={16} bg="white" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }} gap="md">
              <Select
                label="Timezone"
                placeholder="Select time zone"
                data={generateTimezoneOptions()}
                value={selectedTimezone}
                onChange={(val) => val && setSelectedTimezone(val)}
                searchable
              />
              <SchedulingFilter
                showAppointments={showAppointments}
                onShowAppointmentsChange={setShowAppointments}
                showAvailability={showAvailability}
                onShowAvailabilityChange={setShowAvailability}
                onServiceTypeChange={setSelectedServiceType}
                selectedServiceType={selectedServiceType}
                onStatusesChange={setSelectedStatuses}
                selectedStatuses={selectedStatuses}
                onLocationsChange={setSelectedLocation}
                selectedLocations={selectedLocation}
                locations={locationNames}
                activityDefinitionsList={activityDefinitionsList}
                roles={PROVIDER_ROLE_OPTIONS}
                onProvidersChange={setSelectedProvider}
                onRolesChange={setSelectedRole}
                selectedProviders={selectedProvider}
                selectedRoles={selectedRole}
                practitioners={practitionersWithRoleLabels}
                practitionerRoles={practitionerRoles}
              />
              <DatePicker
                mt="md"
                size="sm"
                value={date}
                firstDayOfWeek={0}
                onChange={(value) => {
                  if (!value) return;
                  if (typeof value === 'string') {
                    const [year, month, day] = value.split('-').map(Number);
                    setDate(new Date(year, month - 1, day));
                  } else {
                    setDate(value);
                  }
                }}
              />
            </Stack>
          </Stack>
        )}
        <Box className={`${classes.calendarWrapper} ${view === 'day' ? classes.dayViewNoTodayFill : ''}`}>
          <LoadingOverlay
            visible={appointmentsLoading || blockSlotsLoading || isAvailabilityLoading}
            zIndex={10}
            overlayProps={{ blur: 2 }}
          />
          <Calendar<CalendarEvent, object>
            ref={calendarRef}
            components={{ toolbar: ToolbarWrapper }}
            formats={{
              eventTimeRangeFormat: () => '',
              timeGutterFormat: (date, _culture, loc) => (loc ?? localizer).format(date, 'h:mm a'),
            }}
            showMultiDayTimes={view === 'day' || view === 'week'}
            view={view}
            date={calendarAnchorDate}
            localizer={localizer}
            events={showAppointments ? calendarEvents : []}
            backgroundEvents={showAvailability ? backgroundEventsForCalendar : []}
            eventPropGetter={eventStyleGetter}
            dayLayoutAlgorithm="no-overlap"
            min={dayViewBounds.min}
            max={dayViewBounds.max}
            {...(dayViewCalendarProps ?? {})}
            onNavigate={onNavigate}
            onRangeChange={() => {}}
            onSelectSlot={onSelectSlot}
            onSelectEvent={(event, e) => handleSelectEvent(event, e)}
            popup
            selectable={false}
          />
        </Box>
        {selectedAppointment && popoverPosition && (
          <AppointmentDetailPopover
            opened={popoverOpened}
            onClose={handleClosePopover}
            appointment={selectedAppointment}
            selectedTimezone={selectedTimezone}
            onDelete={handleDeleteAppointment}
            onStatusChange={handleStatusChange}
            position={popoverPosition}
            onEdit={handleEditAppointment}
          />
        )}
        {selectedBlock && popoverPosition && (
          <BlockDetailPopover
            opened={blockPopoverOpened}
            onClose={handleCloseBlockPopover}
            block={selectedBlock}
            selectedTimezone={selectedTimezone}
            position={popoverPosition}
            onEdit={handleEditBlock}
            onDeleted={() => {
              refreshBlockSlots();
              refreshAvailability();
            }}
          />
        )}

        <AppointmentDrawer
          opened={appointmentDrawerOpened}
          onClose={() => {
            setAppointmentDrawerOpened(false);
            setEditAppointmentContext(null);
          }}
          practitioners={practitionersWithRoleLabels}
          appointmentReference={editAppointmentContext?.appointmentRef ?? null}
        />
        <BlockDrawer
          opened={blockDrawerOpened}
          onClose={() => {
            setBlockDrawerOpened(false);
            setEditBlockContext(null);
          }}
          onSaveSuccess={handleBlockSaveSuccess}
          practitioners={practitionersWithRoleLabels}
          practitionerRoles={practitionerRoles}
          slotReference={editBlockContext?.slotRef ?? null}
          detachFromSeries={editBlockContext?.detachFromSeries ?? false}
        />
        <AvailabilityDrawer
          opened={availabilityDrawerOpened}
          onClose={handleCloseAvailabilityDrawer}
          onSaveSuccess={handleAvailabilitySaveSuccess}
          practitioners={practitionersWithRoleLabels}
          {...availabilityDrawerEditProps}
        />
      </Box>
    </SchedulingPageProvider>
  );
}
